import { useCallback } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { Route } from '@/routes/_authenticated/setup/workflows';
import { WorkflowEditor } from './WorkflowEditor';
import { WorkflowList } from './WorkflowList';
import { searchFor, viewOf, type EditorView, type WorkflowsSearch } from './search';

/**
 * The workflow builder (ADR-275). One route, two views: the list, and a
 * workflow open in the editor — a plain URL an author can share, as are the
 * step selected in it and a test open over it (`search.ts`).
 */
export default function WorkflowsPage(): React.ReactElement {
  const search = Route.useSearch();
  const { id } = search;
  const navigate = useNavigate();
  const open = (workflowId: string): void => {
    void navigate({ to: '/setup/workflows', search: { id: workflowId } });
  };
  const close = (): void => {
    void navigate({ to: '/setup/workflows', search: {} });
  };
  // Selecting a step or opening a test changes what the editor shows, not
  // the page: the URL is rewritten in place, so Back leaves the editor rather
  // than walking through every click. The change applies to the URL as it is
  // when it lands, so two in one handler (select, then open a test) both stick.
  const changeView = useCallback(
    (change: Partial<EditorView>): void => {
      if (!id) return;
      void navigate({
        to: '/setup/workflows',
        search: (prev: WorkflowsSearch) => searchFor(id, { ...viewOf(prev), ...change }),
        replace: true,
      });
    },
    [id, navigate]
  );
  // Keyed so opening another workflow remounts the editor with its state.
  if (id) {
    return (
      <WorkflowEditor key={id} id={id} view={viewOf(search)} onView={changeView} onClose={close} />
    );
  }
  return <WorkflowList onOpen={open} />;
}
